/**
 * 앱 배포(QR) 등록 API — 관리 콘솔 (2026-09-14 · 계약 `docs/api/app-releases-contract-2026-09-14.md` §4.1 「안 A」).
 *
 * 서버 SSOT: `apps/chat-server/src/modules/system/app-releases-admin.routes.ts` (SYSTEM_ADMIN 전용).
 * BFF 통로: admin-frontend `/api/system/app-releases*` → `admin/backend/src/routes/app-releases.ts` → chat-server `/api/v1/system/…`.
 * 기관별 배포 방식은 catch-all(`/system/tenants` 허용)로 간다.
 *
 * ★ 설치 파일은 이 모듈의 `apiClient` 를 타지 않는다 — 서버가 준 **presigned PUT** 으로 브라우저가 MinIO 에 직접
 *   올린다(`uploadToPresignedUrl`). 헤더 없는 서명이라 Content-Type 도 붙이지 않는다(붙이면 서명이 깨진다).
 * ★ 검사(패키지 · 번호 증가 · 지문 · 크기 · zip · sha256)는 전부 서버가 한다. 화면은 올리기 전에 meta 와 파일이
 *   맞는지만 먼저 본다(`appReleaseIntake.ts`) — 200MB 를 올린 뒤에 「meta 가 틀렸다」를 듣지 않으려는 것이다.
 */
import { apiClient } from './client';

export type AppReleasePlatform = 'ANDROID' | 'IOS';
export type AppReleaseStatus = 'PENDING' | 'DRAFT' | 'PUBLISHED' | 'REVOKED';
export type AppDistributionMode = 'QR' | 'MDM';

export interface AdminAppRelease {
  id: string;
  platform: AppReleasePlatform;
  packageName: string;
  versionName: string;
  versionCode: number;
  buildNumber: string | null;
  fileName: string;
  sizeBytes: number;
  sha256: string;
  signingCertSha256: string | null;
  minSupportedVersionCode: number | null;
  releaseNotes: string;
  status: AppReleaseStatus;
  publishedAt: string | null;
  revokedAt: string | null;
  createdBy: { id: string; name: string | null };
  createdAt: string;
  updatedAt: string;
}

/** `app-meta.json` — gradle `writeAppMetaRelease` 산출물. 서버 `validateMeta` 가 같은 칸을 본다. */
export interface AppReleaseMeta {
  package: string;
  versionName: string;
  versionCode: number;
  certSha256: string;
  sha256: string;
  size: number;
}

export interface CreateAppReleaseBody {
  platform: 'android' | 'ios';
  meta: AppReleaseMeta;
  releaseNotes?: string;
  minSupportedVersionCode?: number | null;
  buildNumber?: string | null;
}

export interface CreateAppReleaseResult {
  release: AdminAppRelease;
  upload: { method: 'PUT'; uploadUrl: string; expiresAt: string };
}

export interface AppReleaseTransitionResult {
  release: AdminAppRelease;
  autoRevoked?: Array<{ id: string; versionCode: number }>;
}

export async function listAppReleases(platform?: 'android' | 'ios'): Promise<AdminAppRelease[]> {
  const { data } = await apiClient.get<{ releases: AdminAppRelease[] }>('/system/app-releases', {
    params: platform ? { platform } : undefined,
  });
  return data.releases ?? [];
}

export async function createAppRelease(body: CreateAppReleaseBody): Promise<CreateAppReleaseResult> {
  const { data } = await apiClient.post<CreateAppReleaseResult>('/system/app-releases', body);
  return data;
}

export async function finalizeAppRelease(id: string, publish: boolean): Promise<AppReleaseTransitionResult> {
  const { data } = await apiClient.post<AppReleaseTransitionResult>(
    `/system/app-releases/${encodeURIComponent(id)}/finalize`,
    publish ? { publish: true } : {},
  );
  return data;
}

export async function publishAppRelease(id: string): Promise<AppReleaseTransitionResult> {
  const { data } = await apiClient.post<AppReleaseTransitionResult>(`/system/app-releases/${encodeURIComponent(id)}/publish`);
  return data;
}

export async function revokeAppRelease(id: string): Promise<AppReleaseTransitionResult> {
  const { data } = await apiClient.post<AppReleaseTransitionResult>(`/system/app-releases/${encodeURIComponent(id)}/revoke`);
  return data;
}

export interface TenantAppDistribution {
  institutionId: string;
  mode: AppDistributionMode;
  mdmNote: string | null;
}

export async function getTenantAppDistribution(institutionId: string): Promise<TenantAppDistribution> {
  const { data } = await apiClient.get<TenantAppDistribution>(`/system/tenants/${encodeURIComponent(institutionId)}/app-distribution`);
  return data;
}

export async function setTenantAppDistribution(
  institutionId: string,
  body: { mode: AppDistributionMode; mdmNote?: string | null },
): Promise<TenantAppDistribution> {
  const { data } = await apiClient.patch<TenantAppDistribution>(`/system/tenants/${encodeURIComponent(institutionId)}/app-distribution`, body);
  return data;
}

/**
 * presigned PUT 으로 파일을 직접 올린다 — 진행률이 필요해 XMLHttpRequest 를 쓴다(fetch 는 올리는 진행률을 못 준다).
 * ★ 헤더를 하나도 붙이지 않는다(서명이 헤더 없이 만들어졌다). 쿠키도 보내지 않는다(다른 origin · MinIO 는 쿠키를 모른다).
 * 거절(4xx · 5xx)은 상태 코드를 담아 던진다 — 413 이면 엣지 본문 한도(300MB 이상이어야 한다 · 계약 §4.2).
 */
export function uploadToPresignedUrl(
  uploadUrl: string,
  file: Blob,
  onProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.withCredentials = false;
    xhr.upload.onprogress = (ev) => {
      if (onProgress && ev.lengthComputable) onProgress(ev.loaded, ev.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new UploadError(xhr.status));
    };
    xhr.onerror = () => reject(new UploadError(0));
    xhr.onabort = () => reject(new UploadError(0, true));
    xhr.send(file);
  });
}

export class UploadError extends Error {
  constructor(readonly status: number, readonly aborted = false) {
    super(
      aborted ? '업로드를 취소했습니다.'
        : status === 413 ? '파일이 너무 커서 서버(엣지)가 거절했습니다 — 엣지 본문 한도가 300MB 이상이어야 합니다.'
        : status === 403 ? '업로드 주소가 만료됐거나 서명이 맞지 않습니다 — 등록을 처음부터 다시 하세요(5분 안에 올려야 합니다).'
        : status === 0 ? '업로드 연결이 끊겼습니다 — 네트워크 또는 CORS 를 확인하세요.'
        : `업로드 실패 (HTTP ${status})`,
    );
    this.name = 'UploadError';
  }
}
